/**
 * Offline Database Service - IndexedDB wrapper for offline storage
 * Stores pending sales, orders, and sync queue
 */

const DB_NAME = 'menumaster_offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  PENDING_SALES: 'pending_sales',
  PENDING_ORDERS: 'pending_orders',
  SYNC_QUEUE: 'sync_queue',
  CACHED_DATA: 'cached_data',
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      console.log('IndexedDB initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Pending Sales store
      if (!database.objectStoreNames.contains(STORES.PENDING_SALES)) {
        const salesStore = database.createObjectStore(STORES.PENDING_SALES, { keyPath: 'localId' });
        salesStore.createIndex('createdAt', 'createdAt', { unique: false });
        salesStore.createIndex('status', 'status', { unique: false });
      }

      // Pending Orders store
      if (!database.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
        const ordersStore = database.createObjectStore(STORES.PENDING_ORDERS, { keyPath: 'localId' });
        ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
        ordersStore.createIndex('tableId', 'tableId', { unique: false });
      }

      // Sync Queue store
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
        syncStore.createIndex('status', 'status', { unique: false });
      }

      // Cached Data store (for products, categories, etc.)
      if (!database.objectStoreNames.contains(STORES.CACHED_DATA)) {
        const cacheStore = database.createObjectStore(STORES.CACHED_DATA, { keyPath: 'key' });
        cacheStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      console.log('IndexedDB stores created');
    };
  });
};

/**
 * Generic CRUD operations for IndexedDB
 */
const getStore = (storeName, mode = 'readonly') => {
  if (!db) {
    throw new Error('Database not initialized. Call initOfflineDB first.');
  }
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

// Add item to store
export const addToStore = (storeName, data) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readwrite');
      const request = store.add(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

// Get item from store
export const getFromStore = (storeName, key) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readonly');
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

// Get all items from store
export const getAllFromStore = (storeName) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readonly');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

// Update item in store
export const updateInStore = (storeName, data) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readwrite');
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

// Delete item from store
export const deleteFromStore = (storeName, key) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readwrite');
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

// Clear all items from store
export const clearStore = (storeName) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readwrite');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

// Get items by index
export const getByIndex = (storeName, indexName, value) => {
  return new Promise((resolve, reject) => {
    try {
      const store = getStore(storeName, 'readonly');
      const index = store.index(indexName);
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Specific operations for offline sales/orders
 */

// Generate a local ID for offline records
export const generateLocalId = () => {
  return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Add pending sale
export const addPendingSale = async (saleData) => {
  const localId = generateLocalId();
  const pendingSale = {
    localId,
    ...saleData,
    createdAt: new Date().toISOString(),
    status: 'pending',
    syncAttempts: 0,
  };
  
  await addToStore(STORES.PENDING_SALES, pendingSale);
  
  // Also add to sync queue
  await addToSyncQueue('sale', pendingSale);
  
  return localId;
};

// Get all pending sales
export const getPendingSales = () => {
  return getAllFromStore(STORES.PENDING_SALES);
};

// Add pending order
export const addPendingOrder = async (orderData) => {
  const localId = generateLocalId();
  const pendingOrder = {
    localId,
    ...orderData,
    createdAt: new Date().toISOString(),
    status: 'pending',
    syncAttempts: 0,
  };
  
  await addToStore(STORES.PENDING_ORDERS, pendingOrder);
  
  // Also add to sync queue
  await addToSyncQueue('order', pendingOrder);
  
  return localId;
};

// Get all pending orders
export const getPendingOrders = () => {
  return getAllFromStore(STORES.PENDING_ORDERS);
};

// Update pending order
export const updatePendingOrder = async (localId, updates) => {
  const existing = await getFromStore(STORES.PENDING_ORDERS, localId);
  if (existing) {
    const updated = { ...existing, ...updates, updatedAt: new Date().toISOString() };
    await updateInStore(STORES.PENDING_ORDERS, updated);
    return updated;
  }
  return null;
};

// Add to sync queue
export const addToSyncQueue = async (type, data) => {
  const queueItem = {
    type, // 'sale', 'order', 'order_update'
    data,
    createdAt: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    lastAttempt: null,
    error: null,
  };
  
  return addToStore(STORES.SYNC_QUEUE, queueItem);
};

// Get pending sync items
export const getPendingSyncItems = () => {
  return getByIndex(STORES.SYNC_QUEUE, 'status', 'pending');
};

// Update sync item status
export const updateSyncItemStatus = async (id, status, error = null) => {
  const item = await getFromStore(STORES.SYNC_QUEUE, id);
  if (item) {
    item.status = status;
    item.lastAttempt = new Date().toISOString();
    item.attempts += 1;
    if (error) item.error = error;
    await updateInStore(STORES.SYNC_QUEUE, item);
  }
};

// Remove synced item
export const removeSyncedItem = (id) => {
  return deleteFromStore(STORES.SYNC_QUEUE, id);
};

// Remove synced sale
export const removeSyncedSale = (localId) => {
  return deleteFromStore(STORES.PENDING_SALES, localId);
};

// Remove synced order
export const removeSyncedOrder = (localId) => {
  return deleteFromStore(STORES.PENDING_ORDERS, localId);
};

/**
 * Cache management for offline data
 */

// Cache data (products, categories, tables, etc.)
export const cacheData = async (key, data) => {
  const cacheItem = {
    key,
    data,
    updatedAt: new Date().toISOString(),
  };
  return updateInStore(STORES.CACHED_DATA, cacheItem);
};

// Get cached data
export const getCachedData = async (key) => {
  const item = await getFromStore(STORES.CACHED_DATA, key);
  return item?.data || null;
};

// Get cache timestamp
export const getCacheTimestamp = async (key) => {
  const item = await getFromStore(STORES.CACHED_DATA, key);
  return item?.updatedAt || null;
};

/**
 * Statistics
 */
export const getOfflineStats = async () => {
  const pendingSales = await getPendingSales();
  const pendingOrders = await getPendingOrders();
  const syncQueue = await getPendingSyncItems();
  
  return {
    pendingSales: pendingSales.length,
    pendingOrders: pendingOrders.length,
    pendingSync: syncQueue.length,
    totalPending: pendingSales.length + pendingOrders.length,
  };
};

export { STORES };
